/**
 * Auth API methods: login, register, refresh, OAuth, profile, and MFA.
 */

import apiClient from './client';
import type {
  AuthTokens,
  LoginRequest,
  LoginResponse,
  MfaEnableResponse,
  MfaSetupResponse,
  OAuthProvider,
  PasswordChangeRequest,
  ProfileUpdateRequest,
  RegisterRequest,
  User,
} from '@/types/auth';

/** Authenticate with email and password. */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/v1/auth/login', credentials);
  return data;
}

/** Register a new user (platform admin only). */
export async function register(payload: RegisterRequest): Promise<User> {
  const { data } = await apiClient.post<User>('/v1/auth/register', payload);
  return data;
}

/** Exchange a refresh token for a new token pair. */
export async function refreshTokens(refreshToken: string): Promise<AuthTokens> {
  const { data } = await apiClient.post<AuthTokens>('/v1/auth/refresh', null, {
    headers: { Authorization: `Bearer ${refreshToken}` },
  });
  return data;
}

/** Get the OAuth authorization URL for the given provider. */
export async function getOAuthUrl(provider: OAuthProvider): Promise<string> {
  const { data } = await apiClient.get<{ authorization_url: string }>(
    `/v1/auth/oauth/${provider}/authorize`,
  );
  return data.authorization_url;
}

/** Fetch the current authenticated user's profile. */
export async function getMe(): Promise<User> {
  const { data } = await apiClient.get<User>('/v1/auth/me');
  return data;
}

export async function logout(accessToken: string): Promise<void> {
  await apiClient.post('/v1/auth/logout', null, {headers:{Authorization:`Bearer ${accessToken}`}});
}

// ---------------------------------------------------------------------------
// Profile management
// ---------------------------------------------------------------------------

/** Update the authenticated user's profile. */
export async function updateProfile(payload: ProfileUpdateRequest): Promise<User> {
  const { data } = await apiClient.put<User>('/v1/auth/profile', payload);
  return data;
}

/** Change the authenticated user's password. */
export async function changePassword(payload: PasswordChangeRequest): Promise<void> {
  await apiClient.put('/v1/auth/password', payload);
}

/** Upload a profile picture. */
export async function uploadAvatar(file: File): Promise<{ avatar_url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<{ avatar_url: string }>('/v1/auth/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Remove the profile picture. */
export async function deleteAvatar(): Promise<void> {
  await apiClient.delete('/v1/auth/avatar');
}

// ---------------------------------------------------------------------------
// MFA
// ---------------------------------------------------------------------------

/** Start MFA setup — returns a TOTP secret and provisioning URI. */
export async function setupMfa(): Promise<MfaSetupResponse> {
  const { data } = await apiClient.post<MfaSetupResponse>('/v1/auth/mfa/setup');
  return data;
}

/** Verify a TOTP code and enable MFA. Returns recovery codes. */
export async function enableMfa(code: string): Promise<MfaEnableResponse> {
  const { data } = await apiClient.post<MfaEnableResponse>('/v1/auth/mfa/enable', { code });
  return data;
}

/** Disable MFA (requires password confirmation). */
export async function disableMfa(password: string): Promise<void> {
  await apiClient.post('/v1/auth/mfa/disable', { password });
}

/** Verify a TOTP code during MFA login. */
export async function verifyMfa(mfaToken: string, code: string): Promise<AuthTokens> {
  const { data } = await apiClient.post<AuthTokens>('/v1/auth/mfa/verify', {
    mfa_token: mfaToken,
    code,
  });
  return data;
}

/** Use a recovery code during MFA login. */
export async function useRecoveryCode(mfaToken: string, recoveryCode: string): Promise<AuthTokens> {
  const { data } = await apiClient.post<AuthTokens>('/v1/auth/mfa/recovery', {
    mfa_token: mfaToken,
    recovery_code: recoveryCode,
  });
  return data;
}
